import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { documentApi, productApi, customerApi, metaApi } from '../../api/endpoints.js';
import { AttachmentPicker } from '../../components/Attachments.jsx';
import { RichTextToolbar } from '../../components/RichText.jsx';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';
import Modal from '../../components/Modal.jsx';
import { Field, Select, TextInput, TextArea } from '../../components/Field.jsx';
import { formatCurrency, formatDate, isoDay } from '../../utils/format.js';
import { DOCUMENT_TYPES, DOCUMENT_STATUSES } from '../../constants.js';

const EMPTY_LINE = { name: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, vatRate: 20, unit: 'unit' };

/**
 * Quote / invoice editor.
 *
 * The totals shown here are computed locally for instant feedback, but they are
 * only ever a preview: the server recomputes them from the lines on save, and
 * its numbers are the ones stored. That keeps the display snappy without ever
 * trusting the browser with money.
 */
const DocumentEditorPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const isNew = !id || id === 'new';
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState(null);
  const [sending, setSending] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const messageRef = useRef(null);

  // Static per deployment, so it comes from /api/meta rather than a poll.
  const { data: meta } = useApi(() => metaApi.get(), []);
  const mail = meta?.mail;

  // Whether this document is frozen, and the fingerprint of the stored file.
  const { data: archive, refresh: refreshArchive } = useApi(
    () => (isNew ? Promise.resolve(null) : documentApi.archive(id)),
    [id],
  );

  const { data: products } = useApi(() => productApi.list(), []);
  const { data: customers } = useApi(() => customerApi.list({ pageSize: 200 }), []);

  useEffect(() => {
    if (isNew) {
      setDoc({
        type: params.get('type') || 'QUOTE',
        customerId: params.get('customerId') || '',
        issueDate: isoDay(),
        validUntil: '',
        dueDate: '',
        lines: [{ ...EMPTY_LINE }],
        notes: '',
        terms: '',
        status: 'DRAFT',
      });
      return;
    }
    documentApi
      .get(id)
      .then(setDoc)
      .catch((err) => toast.error(toMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totals = useMemo(() => {
    const lines = doc?.lines ?? [];
    let subtotal = 0;
    let discount = 0;
    let vat = 0;
    for (const line of lines) {
      const gross = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
      const lineDiscount = (gross * (Number(line.discountPercent) || 0)) / 100;
      const net = gross - lineDiscount;
      subtotal += gross;
      discount += lineDiscount;
      vat += (net * (Number(line.vatRate) || 0)) / 100;
    }
    const net = subtotal - discount;
    return { subtotal, discount, net, vat, total: net + vat };
  }, [doc]);

  if (!doc) return <Spinner full />;

  const frozen = doc.type === 'INVOICE' && doc.status !== 'DRAFT';

  const patchLine = (index, part) =>
    setDoc((current) => ({
      ...current,
      lines: current.lines.map((line, i) => (i === index ? { ...line, ...part } : line)),
    }));

  const addLine = () => setDoc((c) => ({ ...c, lines: [...c.lines, { ...EMPTY_LINE }] }));
  const removeLine = (index) => setDoc((c) => ({ ...c, lines: c.lines.filter((_, i) => i !== index) }));

  /** Picking a catalogue item fills the line - the price stays editable. */
  const applyProduct = (index, productId) => {
    const product = (products?.items ?? []).find((p) => p.id === productId);
    if (!product) return patchLine(index, { productId: null });
    patchLine(index, {
      productId: product.id,
      reference: product.reference,
      name: product.name,
      description: product.description,
      unit: product.unit,
      unitPrice: product.unitPrice,
      vatRate: product.vatRate,
    });
  };

  const save = async () => {
    /**
     * A row with no designation used to be dropped here without a word. Fill in
     * a quantity and a price, forget the designation, save - and the quote came
     * back empty with no explanation. So now only a genuinely BLANK row is
     * dropped (you added it and changed your mind); a row carrying figures
     * stops the save and says which one.
     */
    const isBlank = (line) =>
      !line.name?.trim() && !Number(line.quantity) && !Number(line.unitPrice) && !line.description?.trim();
    const lines = doc.lines.filter((line) => !isBlank(line));

    const unnamed = lines.findIndex((line) => !line.name?.trim());
    if (unnamed >= 0) {
      toast.error(t('documents.lineNeedsName', { row: unnamed + 1 }));
      return;
    }

    setBusy(true);
    try {
      const body = {
        type: doc.type,
        customerId: doc.customerId || null,
        issueDate: doc.issueDate || undefined,
        dueDate: doc.dueDate || null,
        validUntil: doc.validUntil || null,
        lines,
        notes: doc.notes,
        terms: doc.terms,
      };
      const saved = isNew ? await documentApi.create(body) : await documentApi.update(id, body);
      toast.success(t('common.save'));
      navigate(`/documents/${saved.id}`, { replace: true });
      if (!isNew) setDoc(await documentApi.get(saved.id));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** Fetched with the token, like any other private file, then handed over. */
  const downloadPdf = async () => {
    try {
      await documentApi.pdf(id, `${doc.number}.pdf`);
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  /**
   * Cancel an issued invoice the only lawful way: a credit note for the same
   * amount, then a fresh draft to correct. The API does all three steps in one
   * call, so there is no half-done state to land in.
   *
   * On success we go straight to the replacement draft - that is what the
   * person clicking "cancel and fix" actually wants to be looking at.
   */
  const submitCancel = async () => {
    setBusy(true);
    try {
      const result = await documentApi.cancel(id, {
        reason: cancelling.reason.trim(),
        reissue: cancelling.reissue,
        notify: cancelling.notify,
      });

      /**
       * The cancellation succeeded either way - the avoir exists and the
       * invoice is void. Whether the customer has been TOLD is a separate fact,
       * and the one thing a person cannot check from this screen, so it is said
       * out loud rather than folded into a generic success message.
       */
      const email = result.creditNoteEmail ?? { sent: false, reason: 'not-requested' };
      const numbers = {
        creditNote: result.creditNote.number,
        replacement: result.replacement ? result.replacement.number : '-',
      };
      if (email.sent) {
        toast.success(t('documents.cancelledBySent', { ...numbers, to: email.to }));
      } else if (cancelling.notify) {
        toast.error(
          t('documents.cancelledByNotSent', {
            ...numbers,
            reason: t(`documents.creditNoteEmailReason.${email.reason}`, email.reason),
          }),
        );
      } else {
        toast.success(t('documents.cancelledBy', numbers));
      }
      setCancelling(null);
      if (result.replacement) navigate(`/documents/${result.replacement.id}`);
      else {
        setDoc(await documentApi.get(id));
        refreshArchive();
      }
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitSend = async () => {
    setBusy(true);
    try {
      const result = await documentApi.send(id, {
        to: sending.to.trim(),
        subject: sending.subject.trim() || undefined,
        message: sending.message,
        attachmentIds: sending.files.map((file) => file.id),
      });
      toast.success(t('documents.sentTo', { to: result.to }));
      setSending(null);
      // The status moved to SENT server side; reflect it without a reload.
      setDoc((current) => ({ ...current, status: result.status }));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      const saved = await documentApi.update(id, { status });
      setDoc({ ...doc, status: saved.status });
      toast.success(t(`documents.statuses.${status}`));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const convert = async (type) => {
    try {
      const created = await documentApi.convert(id, type);
      toast.success(created.number);
      navigate(`/documents/${created.id}`);
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const submitPayment = async () => {
    try {
      const saved = await documentApi.payment(id, Number(payment));
      setDoc({ ...doc, status: saved.status, paidAmount: saved.paidAmount });
      setPayment(null);
      toast.success(t('documents.paymentRecorded'));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title={isNew ? t(`documents.new.${doc.type}`) : doc.number}
        subtitle={isNew ? '' : `${t(`documents.types.${doc.type}`)} - ${t(`documents.statuses.${doc.status}`)}`}
        actions={
          <div className="header-actions">
            <button type="button" className="btn" onClick={() => navigate('/documents')}>
              {t('common.back')}
            </button>
            {!isNew ? (
              <>
                {/* The same bytes the customer receives, not a browser print. */}
                <button type="button" className="btn" onClick={downloadPdf}>
                  {t('documents.downloadPdf')}
                </button>
                <button type="button" className="btn" onClick={() => window.open(`/documents/${id}/print`, '_blank')}>
                  {t('documents.print')}
                </button>
              </>
            ) : null}
            {/* Only drawn where sending is actually possible: the deployment has
                an MTA and an administrator has switched the mailbox on. A button
                that always fails is worse than no button. */}
            {/* Nothing to send while the document has no line - and saying so
                on the button beats a refusal after the click. */}
            {!isNew && mail?.enabled ? (
              <button
                type="button"
                className="btn"
                disabled={!doc.lines?.length}
                title={doc.lines?.length ? undefined : t('documents.noLineToSend')}
                onClick={() =>
                  setSending({
                    to: doc.customerSnapshot?.email ?? '',
                    subject: '',
                    message: '',
                    files: [],
                  })
                }
              >
                {t('documents.sendByEmail')}
              </button>
            ) : null}
            {!isNew && doc.type === 'QUOTE' ? (
              <button type="button" className="btn" onClick={() => convert('INVOICE')}>
                {t('documents.toInvoice')}
              </button>
            ) : null}
            {!isNew && doc.type === 'INVOICE' ? (
              <>
                <button type="button" className="btn" onClick={() => setPayment('')}>
                  {t('documents.recordPayment')}
                </button>
                <button type="button" className="btn" onClick={() => convert('RECEIPT')}>
                  {t('documents.toReceipt')}
                </button>
              </>
            ) : null}
            {!frozen ? (
              <button type="button" className="btn btn--primary" onClick={save} disabled={busy}>
                {busy ? t('common.saving') : t('common.save')}
              </button>
            ) : null}
          </div>
        }
      />

      {/* The notice used to say "cancel it" without offering anywhere to do so.
          The button is now in the banner, where the sentence sends you. */}
      {frozen ? (
        <div className="banner banner--warning banner--action">
          <span>
            {t('documents.frozen')}
            {archive?.archived ? (
              <span className="banner__meta">
                {' '}
                {t('documents.archivedOn', { date: formatDate(archive.issuedAt, locale) })} -{' '}
                {t('documents.fingerprint')} {archive.sha256.slice(0, 16)}...
              </span>
            ) : null}
          </span>
          {doc.status !== 'CANCELLED' ? (
            <button
              type="button"
              className="btn btn--small"
              onClick={() => setCancelling({ reason: '', reissue: true, notify: true })}
            >
              {t('documents.cancelAndFix')}
            </button>
          ) : null}
        </div>
      ) : null}

      {doc.status === 'CANCELLED' ? (
        <p className="banner banner--danger">
          {t('documents.cancelledOn', {
            date: doc.cancelledAt ? formatDate(doc.cancelledAt, locale) : '-',
          })}
          {doc.cancelReason ? ` - ${doc.cancelReason}` : ''}
        </p>
      ) : null}

      <div className="grid grid--2">
        <Card title={t('documents.details')}>
          <div className="form-grid">
            <Field label={t('documents.type')} htmlFor="d-type" required>
              <Select
                id="d-type"
                value={doc.type}
                onChange={(v) => setDoc({ ...doc, type: v })}
                options={DOCUMENT_TYPES.map((type) => ({ value: type, label: t(`documents.types.${type}`) }))}
                disabled={!isNew}
              />
            </Field>
            <Field label={t('customers.companyName')} htmlFor="d-customer" required>
              <Select
                id="d-customer"
                value={doc.customerId ?? ''}
                onChange={(v) => setDoc({ ...doc, customerId: v })}
                placeholder={t('common.none')}
                options={(customers?.items ?? []).map((c) => ({ value: c.id, label: c.companyName }))}
                disabled={frozen}
              />
            </Field>
            <Field label={t('documents.issueDate')} htmlFor="d-issue">
              <TextInput
                id="d-issue"
                type="date"
                value={doc.issueDate ?? ''}
                onChange={(v) => setDoc({ ...doc, issueDate: v })}
                disabled={frozen}
              />
            </Field>
            {doc.type === 'QUOTE' ? (
              <Field label={t('documents.validUntil')} htmlFor="d-valid">
                <TextInput
                  id="d-valid"
                  type="date"
                  value={doc.validUntil ?? ''}
                  onChange={(v) => setDoc({ ...doc, validUntil: v })}
                />
              </Field>
            ) : (
              <Field label={t('documents.dueDate')} htmlFor="d-due">
                <TextInput
                  id="d-due"
                  type="date"
                  value={doc.dueDate ?? ''}
                  onChange={(v) => setDoc({ ...doc, dueDate: v })}
                  disabled={frozen}
                />
              </Field>
            )}
            {!isNew ? (
              <Field label={t('common.status')} htmlFor="d-status">
                <Select
                  id="d-status"
                  value={doc.status}
                  onChange={changeStatus}
                  options={DOCUMENT_STATUSES.map((s) => ({ value: s, label: t(`documents.statuses.${s}`) }))}
                />
              </Field>
            ) : null}
          </div>
        </Card>

        <Card title={t('documents.totals')}>
          <dl className="totals">
            <div>
              <dt>{t('documents.subtotal')}</dt>
              <dd>{formatCurrency(totals.subtotal, locale, doc.currency)}</dd>
            </div>
            <div>
              <dt>{t('documents.discount')}</dt>
              <dd>-{formatCurrency(totals.discount, locale, doc.currency)}</dd>
            </div>
            <div>
              <dt>{t('documents.netAmount')}</dt>
              <dd>{formatCurrency(totals.net, locale, doc.currency)}</dd>
            </div>
            <div>
              <dt>{t('documents.vat')}</dt>
              <dd>{formatCurrency(totals.vat, locale, doc.currency)}</dd>
            </div>
            <div className="totals__grand">
              <dt>{t('documents.total')}</dt>
              <dd>{formatCurrency(totals.total, locale, doc.currency)}</dd>
            </div>
            {!isNew && doc.type === 'INVOICE' ? (
              <>
                <div>
                  <dt>{t('documents.paid')}</dt>
                  <dd>{formatCurrency(doc.paidAmount, locale, doc.currency)}</dd>
                </div>
                <div>
                  <dt>{t('documents.balance')}</dt>
                  <dd>
                    <Badge
                      value={Number(doc.total) - Number(doc.paidAmount) <= 0 ? 'PAID' : 'LATE'}
                      label={formatCurrency(Number(doc.total) - Number(doc.paidAmount), locale, doc.currency)}
                    />
                  </dd>
                </div>
              </>
            ) : null}
          </dl>
          <p className="muted small">{t('documents.totalsHint')}</p>
        </Card>
      </div>

      <Card
        title={t('documents.lines')}
        actions={
          !frozen ? (
            <button type="button" className="btn btn--small" onClick={addLine}>
              {t('documents.addLine')}
            </button>
          ) : null
        }
      >
        <div className="table-wrap">
          <table className="table doc-lines">
            <thead>
              <tr>
                <th>{t('documents.catalogue')}</th>
                <th>{t('documents.designation')}</th>
                <th className="cell-right">{t('documents.quantity')}</th>
                <th className="cell-right">{t('documents.unitPrice')}</th>
                <th className="cell-right">%</th>
                <th className="cell-right">{t('documents.vat')}</th>
                <th className="cell-right">{t('documents.lineTotal')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((line, index) => {
                const gross = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
                const net = gross - (gross * (Number(line.discountPercent) || 0)) / 100;
                return (
                  <tr key={index}>
                    <td>
                      <Select
                        value={line.productId ?? ''}
                        onChange={(v) => applyProduct(index, v)}
                        placeholder="-"
                        options={(products?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
                        disabled={frozen}
                      />
                    </td>
                    <td>
                      <TextInput value={line.name} onChange={(v) => patchLine(index, { name: v })} disabled={frozen} />
                      <TextInput
                        value={line.description}
                        onChange={(v) => patchLine(index, { description: v })}
                        placeholder={t('documents.description')}
                        disabled={frozen}
                      />
                    </td>
                    <td className="cell-right">
                      <TextInput
                        type="number"
                        value={line.quantity}
                        onChange={(v) => patchLine(index, { quantity: v })}
                        disabled={frozen}
                      />
                    </td>
                    <td className="cell-right">
                      <TextInput
                        type="number"
                        value={line.unitPrice}
                        onChange={(v) => patchLine(index, { unitPrice: v })}
                        disabled={frozen}
                      />
                    </td>
                    <td className="cell-right">
                      <TextInput
                        type="number"
                        value={line.discountPercent}
                        onChange={(v) => patchLine(index, { discountPercent: v })}
                        disabled={frozen}
                      />
                    </td>
                    <td className="cell-right">
                      <TextInput
                        type="number"
                        value={line.vatRate}
                        onChange={(v) => patchLine(index, { vatRate: v })}
                        disabled={frozen}
                      />
                    </td>
                    <td className="cell-right cell-strong">{formatCurrency(net, locale, doc.currency)}</td>
                    <td>
                      {!frozen ? (
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => removeLine(index)}>
                          x
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid--2">
        <Card title={t('common.notes')}>
          <TextArea value={doc.notes ?? ''} onChange={(v) => setDoc({ ...doc, notes: v })} disabled={frozen} />
        </Card>
        <Card title={t('documents.terms')}>
          <TextArea value={doc.terms ?? ''} onChange={(v) => setDoc({ ...doc, terms: v })} disabled={frozen} />
        </Card>
      </div>

      <Modal
        open={payment !== null}
        title={t('documents.recordPayment')}
        onClose={() => setPayment(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setPayment(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={submitPayment}>
              {t('common.confirm')}
            </button>
          </>
        }
      >
        <Field label={t('documents.amount')} htmlFor="pay-amount">
          <TextInput id="pay-amount" type="number" value={payment ?? ''} onChange={setPayment} />
        </Field>
        <p className="muted small">
          {t('documents.balance')}: {formatCurrency(Number(doc.total) - Number(doc.paidAmount ?? 0), locale, doc.currency)}
        </p>
      </Modal>

      {/* Cancelling is not an undo: it writes a credit note and opens a new
          invoice. The wording says so before the click, not after. */}
      <Modal
        open={cancelling !== null}
        title={t('documents.cancelAndFix')}
        onClose={() => setCancelling(null)}
        width={600}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCancelling(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={submitCancel}
              disabled={busy || (cancelling?.reason.trim().length ?? 0) < 3}
            >
              {busy ? t('common.saving') : t('documents.confirmCancel')}
            </button>
          </>
        }
      >
        {cancelling ? (
          <div className="form-grid">
            <div className="span-2">
              <p className="card__lead">{t('documents.cancelExplain', { number: doc.number })}</p>
            </div>
            <div className="span-2">
              <Field
                label={t('documents.cancelReason')}
                htmlFor="cancel-reason"
                required
                hint={t('documents.cancelReasonHint')}
              >
                <TextArea
                  id="cancel-reason"
                  rows={3}
                  value={cancelling.reason}
                  onChange={(v) => setCancelling({ ...cancelling, reason: v })}
                />
              </Field>
            </div>
            <div className="span-2">
              <label className={cancelling.reissue ? 'option option--checked' : 'option'}>
                <input
                  type="checkbox"
                  checked={cancelling.reissue}
                  onChange={() => setCancelling({ ...cancelling, reissue: !cancelling.reissue })}
                />
                <span>{t('documents.reissue')}</span>
              </label>
              <p className="field__hint">{t('documents.reissueHint')}</p>
            </div>
            {/* Sending is outward-facing, so the address is shown before the
                click, never guessed silently - same rule as the send dialog. */}
            <div className="span-2">
              <label className={cancelling.notify ? 'option option--checked' : 'option'}>
                <input
                  type="checkbox"
                  checked={cancelling.notify}
                  onChange={() => setCancelling({ ...cancelling, notify: !cancelling.notify })}
                />
                <span>{t('documents.notifyCreditNote')}</span>
              </label>
              <p className="field__hint">
                {doc.customerSnapshot?.email
                  ? t('documents.notifyCreditNoteTo', { email: doc.customerSnapshot.email })
                  : t('documents.notifyCreditNoteNoEmail')}
              </p>
              <p className="field__hint">{t('documents.notifyCreditNoteHint')}</p>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Sending is outward-facing: a real person receives this. So the address
          is shown, editable, and never guessed silently. */}
      <Modal
        open={sending !== null}
        title={t('documents.sendByEmail')}
        onClose={() => setSending(null)}
        width={640}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setSending(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={submitSend}
              disabled={busy || !sending?.to?.trim()}
            >
              {busy ? t('common.saving') : t('documents.send')}
            </button>
          </>
        }
      >
        {sending ? (
          <div className="form-grid">
            <div className="span-2">
              <Field label={t('documents.recipient')} htmlFor="send-to" required hint={t('documents.recipientHint')}>
                <TextInput
                  id="send-to"
                  type="email"
                  value={sending.to}
                  onChange={(v) => setSending({ ...sending, to: v })}
                />
              </Field>
            </div>
            <div className="span-2">
              <Field label={t('documents.subject')} htmlFor="send-subject">
                <TextInput
                  id="send-subject"
                  value={sending.subject}
                  onChange={(v) => setSending({ ...sending, subject: v })}
                  placeholder={`${t(`documents.types.${doc.type}`)} ${doc.number}`}
                />
              </Field>
            </div>
            <div className="span-2">
              <Field label={t('documents.message')} htmlFor="send-message" hint={t('rich.hint')}>
                <RichTextToolbar
                  inputRef={messageRef}
                  value={sending.message}
                  onChange={(v) => setSending({ ...sending, message: v })}
                />
                <TextArea
                  ref={messageRef}
                  id="send-message"
                  rows={6}
                  value={sending.message}
                  onChange={(v) => setSending({ ...sending, message: v })}
                />
              </Field>
            </div>
            <div className="span-2">
              <Field label={t('documents.attachments')} hint={t('documents.attachmentsHint')}>
                <AttachmentPicker
                  value={sending.files}
                  onChange={(files) => setSending({ ...sending, files })}
                  disabled={busy}
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default DocumentEditorPage;
